/* eslint-disable no-debugger, no-console */

import "regenerator-runtime/runtime";
import {
  UPLOAD_CHUNK_SIZE,
  UPLOAD_CHUNKED_GLOBAL_CONCURRENCY,
  UPLOAD_CHUNKED_MIN_SIZE,
  UPLOAD_CHUNKED_PER_FILE_CONCURRENCY,
  MAX_UPLOAD_FILE_SIZE_BYTES,
  MAX_UPLOAD_FILE_SIZE_LABEL,
} from "../util/constants";
import { NUMBER_OF_FILES_PER_CHUNK, GCS_UPLOAD_MODE } from "../util/constants";
import { fileMixins } from "./file";
import { folderMixins } from "./folder";
import {
  CHUNK_TRANSFER_MODE,
  UploadSemaphore,
  chunkBackoffDelay,
  isChunkedUploadEligible,
  isTransientChunkStatus,
  runLimitedChunkTasks,
  sleepForChunkRetry,
} from "../util/uploadChunks";
import { crc32cHexForBlob } from "../util/uploadCrc32c";
import { sanitizeUploadError } from "../util/uploadDiagnostics";

const globalChunkSemaphore = new UploadSemaphore(UPLOAD_CHUNKED_GLOBAL_CONCURRENCY);

async function releaseFetchResponse(response) {
  if (!response || response.bodyUsed) {
    return;
  }
  if (response.body?.cancel) {
    await response.body.cancel().catch(() => null);
    return;
  }
  await response.arrayBuffer?.().catch(() => null);
}

export async function uploadResumable({
  url,
  file,
  contentType,
  chunkSize,
  onChunkUpload,
  onUploadProgress,
  signal,
  isCancelled,
  maxRetries = 3,
  baseBackoffMs = 200,
  maxBackoffMs = 2000,
  calculateCrc = false
}) {
  const total = file.size;
  const probe = await putChunkSession({
    url,
    headers: { "Content-Range": `bytes */${total}` },
    signal,
  });
  const completed = probe.status === 200 || probe.status === 201;
  const startFromProbe = completed
    ? total
    : committedBytesFromRangeHeader(probe.headers?.get("Range"), total);
  const probeStatus = probe.status;
  await releaseFetchResponse(probe);
  if (!completed && probeStatus !== 308) {
    throw new Error(`GCS resumable session probe failed with status ${probeStatus}`);
  }

  let start = startFromProbe;
  const expectedCrc32c = calculateCrc ? await crc32cHexForBlob(file) : null;

  if (start > 0) {
    onChunkUpload({
      totalBytes: total,
      uploadedBytes: start,
      chunkIndex: Math.floor(Math.max(0, start - 1) / chunkSize),
      chunkLength: 0,
    });
  }

  while (start < total) {
    if (isCancelled && isCancelled()) {
      return;
    }
    const end = Math.min(start + chunkSize, total) - 1;
    const chunk = file.slice(start, end + 1);
    let attempt = 0;
    let res = null;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        res = await putChunkSession({
          url,
          headers: {
            "Content-Type": contentType,
            "Content-Range": `bytes ${start}-${end}/${total}`,
          },
          body: chunk,
          signal,
          onTransferProgress: (loadedBytes) => onUploadProgress?.({
            totalBytes: total,
            uploadedBytes: Math.min(total, start + loadedBytes),
            chunkIndex: Math.floor(start / chunkSize),
            chunkLength: chunk.size,
          }),
        });
        await releaseFetchResponse(res);
      } catch (err) {
        if (signal?.aborted) {
          throw err;
        }
        attempt += 1;
        if (attempt > maxRetries) {
          throw err;
        }
        const backoff = Math.min(
          maxBackoffMs,
          baseBackoffMs * Math.pow(2, attempt - 1) + Math.random() * baseBackoffMs
        );
        await new Promise((resolve) => setTimeout(resolve, backoff));
        continue;
      }

      if (res.status === 500 || res.status === 502 || res.status === 503 || res.status === 504) {
        attempt += 1;
        if (attempt > maxRetries) {
          throw new Error(`GCS upload failed with status ${res.status}`);
        }
        const backoff = Math.min(
          maxBackoffMs,
          baseBackoffMs * Math.pow(2, attempt - 1) + Math.random() * baseBackoffMs
        );
        await new Promise((resolve) => setTimeout(resolve, backoff));
        continue;
      }
      break;
    }

    const isFinalChunk = end + 1 >= total;
    if (isFinalChunk && res.status === 308) {
      throw new Error("GCS upload did not complete; final status 308");
    }
    if (!isFinalChunk && (res.status === 200 || res.status === 201)) {
      throw new Error(`GCS upload completed before all bytes were sent with status ${res.status}`);
    }
    if (![200, 201, 308].includes(res.status)) {
      throw new Error(`GCS upload failed with status ${res.status}`);
    }

    onChunkUpload({
      totalBytes: total,
      uploadedBytes: end + 1,
      chunkIndex: Math.floor(start / chunkSize),
      chunkLength: chunk.size,
    });

    start = end + 1;
  }
  return {
    crc32c: expectedCrc32c,
  };
}

function mapUploadError(error) {
  if (!error) {
    return { code: "UPLOAD_UNKNOWN", userMessage: "Upload failed. Please try again." };
  }
  if (error.name === "AbortError") {
    return { code: "UPLOAD_ABORTED", userMessage: "Upload cancelled." };
  }
  const message = error.message || String(error);
  if (error.code === "UPLOAD_FILE_TOO_LARGE" || message.includes("maximum upload size")) {
    return { code: "UPLOAD_FILE_TOO_LARGE", userMessage: message };
  }
  if (message.includes("status 403") || message.includes("status 401")) {
    return { code: "UPLOAD_FORBIDDEN", userMessage: "Upload permission denied or URL expired." };
  }
  if (message.includes("status 404")) {
    return { code: "UPLOAD_URL_NOT_FOUND", userMessage: "Upload URL not found or expired." };
  }
  if (message.includes("status 500") || message.includes("status 502") || message.includes("status 503") || message.includes("status 504")) {
    return { code: "UPLOAD_TRANSIENT", userMessage: "Temporary upload failure. Please retry." };
  }
  return { code: "UPLOAD_FAILED", userMessage: "Upload failed. Please try again." };
}

async function uploadChunkSession({
  chunk,
  file,
  contentType,
  signal,
  onCommittedBytes,
  onTransferProgress,
  probeCommittedBytes = false,
}) {
  const committedBytes = probeCommittedBytes ? await committedBytesForChunkSession(chunk, signal) : 0;
  await onCommittedBytes?.(committedBytes);
  if (committedBytes >= chunk.byteCount) {
    return new Response("", { status: 200 });
  }
  const start = chunk.byteOffset + committedBytes;
  const end = chunk.byteOffset + chunk.byteCount - 1;
  const body = file.slice(start, end + 1);
  const headers = {
    "Content-Type": contentType,
    "Content-Range": `bytes ${committedBytes}-${chunk.byteCount - 1}/${chunk.byteCount}`,
  };
  return await putChunkSession({
    url: chunk.sessionUri,
    headers,
    body,
    signal,
    onTransferProgress: (loadedBytes) => onTransferProgress?.(committedBytes + loadedBytes),
  });
}

function shouldUseXhrChunkSessionPut() {
  return typeof XMLHttpRequest !== "undefined" && process.env.NODE_ENV !== "test";
}

async function putChunkSession({
  url,
  headers,
  body = null,
  signal,
  onTransferProgress,
}) {
  if (shouldUseXhrChunkSessionPut()) {
    return await xhrPutChunkSession({
      url,
      headers,
      body,
      signal,
      onTransferProgress,
    });
  }
  return await fetch(url, {
    method: "PUT",
    headers,
    body,
    signal,
  });
}

function xhrPutChunkSession({
  url,
  headers,
  body = null,
  signal,
  onTransferProgress,
}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;
    const abort = () => {
      if (!settled) {
        xhr.abort();
      }
    };
    xhr.open("PUT", url, true);
    Object.entries(headers || {}).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = (event) => {
      if (Number.isFinite(event.loaded)) {
        onTransferProgress?.(event.loaded);
      }
    };
    xhr.onload = () => {
      settled = true;
      signal?.removeEventListener?.("abort", abort);
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        bodyUsed: true,
        headers: {
          get: (name) => xhr.getResponseHeader(name),
        },
      });
    };
    xhr.onerror = () => {
      settled = true;
      signal?.removeEventListener?.("abort", abort);
      reject(new Error("Chunk upload network error"));
    };
    xhr.onabort = () => {
      settled = true;
      signal?.removeEventListener?.("abort", abort);
      const error = new Error("Chunk upload aborted");
      error.name = "AbortError";
      reject(error);
    };
    signal?.addEventListener?.("abort", abort, { once: true });
    xhr.send(body);
  });
}

function committedBytesFromRangeHeader(rangeHeader, totalBytes) {
  const match = String(rangeHeader || "").match(/bytes=0-(\d+)$/i);
  if (!match) {
    return 0;
  }
  const lastCommittedByte = Number(match[1]);
  if (!Number.isFinite(lastCommittedByte) || lastCommittedByte < 0) {
    return 0;
  }
  return Math.max(0, Math.min(Number(totalBytes || 0), lastCommittedByte + 1));
}

async function committedBytesForChunkSession(chunk, signal) {
  if (!chunk.sessionUri) {
    return 0;
  }
  const response = await putChunkSession({
    url: chunk.sessionUri,
    headers: {
      "Content-Range": `bytes */${chunk.byteCount}`,
    },
    signal,
  });
  const committedBytes = response.status === 200 || response.status === 201
    ? chunk.byteCount
    : committedBytesFromRangeHeader(response.headers?.get("Range"), chunk.byteCount);
  await releaseFetchResponse(response);
  return committedBytes;
}

function uploadChunkSessionRequest(file) {
  return {
    fileSize: file.size,
    chunkSize: UPLOAD_CHUNK_SIZE,
    minimumChunkedUploadSize: UPLOAD_CHUNKED_MIN_SIZE,
    mimeType: file.type,
    requestedConcurrencyHint: UPLOAD_CHUNKED_PER_FILE_CONCURRENCY,
  };
}

function findMatchingChunk(chunks, chunk) {
  return (chunks || []).find((candidate) =>
    candidate.chunkId === chunk.chunkId
      || candidate.chunkIndex === chunk.chunkIndex
      || Number(candidate.byteOffset) === Number(chunk.byteOffset)
  );
}

let GcsUploadModule = null;
async function loadGcsUpload() {
  if (!GcsUploadModule) {
    const mod = await import("gcs-browser-upload");
    GcsUploadModule = mod.default || mod;
  }
  return GcsUploadModule;
}

function cancellable(asyncFn, cancel = (() => null)) {
  let cancelled = false;
  return {
    run: () => asyncFn(() => cancelled).then(result => { return { result, cancelled: cancelled} }),
    cancel: async () => {
      if (!cancelled) {
        cancelled = true;
        await cancel();
      }
    },
  };
}

export const uploadFileMixins = {
  mixins: [fileMixins, folderMixins],
  methods: {
    async uploadChunkedFile({
      file,
      response,
      options,
      itemIndex,
      progressCallback,
      signal,
      isCancelled,
    }) {
      const session = await this.createUploadSession(options.uploadJobId, options.uploadJobLogId, {
        itemId: response.id,
        ...uploadChunkSessionRequest(file),
      });
      if (session.transferMode !== CHUNK_TRANSFER_MODE.CHUNKED) {
        return false;
      }
      const chunks = session.chunks || [];
      const crcVerificationRequired = !!session.crcVerificationRequired;
      const uploadedByChunk = new Array(chunks.length).fill(0);
      const reportProgress = (chunkIndex, uploadedBytes) => {
        uploadedByChunk[chunkIndex] = Math.max(uploadedByChunk[chunkIndex] || 0, uploadedBytes);
        const totalUploaded = uploadedByChunk.reduce((sum, value) => sum + value, 0);
        options.onClientProgress?.({
          totalBytes: file.size,
          uploadedBytes: totalUploaded,
          chunkIndex,
          chunkLength: uploadedBytes,
        });
        const fileProgress = itemIndex + (totalUploaded / file.size);
        if (fileProgress < itemIndex + 1) {
          progressCallback(fileProgress, file.name);
        }
      };
      chunks
        .filter((chunk) => chunk.status === "COMPLETE")
        .forEach((chunk) => reportProgress(chunk.chunkIndex, chunk.committedByteCount || chunk.byteCount));

      const incompleteChunks = chunks
        .filter((chunk) => chunk.status !== "COMPLETE")
        .map((chunk) => async () => {
          let attempt = Number(chunk.attemptCount || 0);
          while (!isCancelled()) {
            attempt += 1;
            try {
              let chunkForAttempt = chunk;
              if (attempt > Number(chunk.attemptCount || 0) + 1) {
                const refreshedSession = await this.createUploadSession(options.uploadJobId, options.uploadJobLogId, {
                  itemId: response.id,
                  ...uploadChunkSessionRequest(file),
                });
                const refreshedChunk = findMatchingChunk(refreshedSession.chunks, chunk);
                if (refreshedChunk?.status === "COMPLETE") {
                  reportProgress(refreshedChunk.chunkIndex, refreshedChunk.committedByteCount || refreshedChunk.byteCount);
                  return;
                }
                chunkForAttempt = refreshedChunk || chunk;
              }
              if (!chunkForAttempt.sessionUri) {
                throw new Error("Chunk upload session URI is missing");
              }
              const uploadResponse = await uploadChunkSession({
                chunk: chunkForAttempt,
                file,
                contentType: file.type || "application/octet-stream",
                signal,
                probeCommittedBytes: attempt > 1
                  || Number(chunkForAttempt.attemptCount || 0) > 0
                  || Number(chunkForAttempt.committedByteCount || 0) > 0,
                onTransferProgress: (uploadedBytes) => {
                  reportProgress(chunkForAttempt.chunkIndex, uploadedBytes);
                },
                onCommittedBytes: async (committedBytes) => {
                  if (committedBytes > 0 && committedBytes < chunkForAttempt.byteCount) {
                    reportProgress(chunkForAttempt.chunkIndex, committedBytes);
                    await this.recordUploadChunkResult(options.uploadJobId, options.uploadJobLogId, chunkForAttempt.chunkId, {
                      status: "IN_PROGRESS",
                      committedByteCount: committedBytes,
                      attemptCount: attempt,
                    });
                  }
                },
              });
              const uploadOk = uploadResponse.ok;
              const uploadStatus = uploadResponse.status;
              await releaseFetchResponse(uploadResponse);
              if (uploadOk) {
                const expectedCrc32c = crcVerificationRequired
                  ? await crc32cHexForBlob(file.slice(
                    chunkForAttempt.byteOffset,
                    chunkForAttempt.byteOffset + chunkForAttempt.byteCount
                  ))
                  : null;
                await this.recordUploadChunkResult(options.uploadJobId, options.uploadJobLogId, chunkForAttempt.chunkId, {
                  status: "COMPLETE",
                  committedByteCount: chunkForAttempt.byteCount,
                  attemptCount: attempt,
                  expectedCrc32c,
                });
                reportProgress(chunkForAttempt.chunkIndex, chunkForAttempt.byteCount);
                return;
              }
              if (!isTransientChunkStatus(uploadStatus) || attempt >= (session.maxAttempts || 5)) {
                const errorCode = `HTTP_${uploadStatus}`;
                const errorMessage = `Chunk upload failed with status ${uploadStatus}`;
                await this.recordUploadChunkResult(options.uploadJobId, options.uploadJobLogId, chunkForAttempt.chunkId, {
                  status: "ERROR",
                  committedByteCount: 0,
                  attemptCount: attempt,
                  errorCode,
                  errorMessage,
                });
                options.onClientStatus?.("ERROR", { errorCode, errorMessage });
                throw new Error(errorMessage);
              }
              const retryOn = new Date(Date.now() + chunkBackoffDelay(attempt, session)).toISOString();
              await this.recordUploadChunkResult(options.uploadJobId, options.uploadJobLogId, chunkForAttempt.chunkId, {
                status: "IN_PROGRESS",
                committedByteCount: 0,
                attemptCount: attempt,
                errorCode: `HTTP_${uploadStatus}`,
                errorMessage: `Transient chunk upload failure with status ${uploadStatus}`,
                nextRetryOn: retryOn,
              });
            } catch (error) {
              if (signal?.aborted || isCancelled()) {
                return;
              }
              if (attempt >= (session.maxAttempts || 5)) {
                const errorMessage = error?.message || "Chunk upload failed";
                await this.recordUploadChunkResult(options.uploadJobId, options.uploadJobLogId, chunk.chunkId, {
                  status: "ERROR",
                  committedByteCount: 0,
                  attemptCount: attempt,
                  errorCode: "UPLOAD_CHUNK_FAILED",
                  errorMessage,
                });
                options.onClientStatus?.("ERROR", { errorCode: "UPLOAD_CHUNK_FAILED", errorMessage });
                throw error;
              }
            }
            await sleepForChunkRetry(chunkBackoffDelay(attempt, session), isCancelled);
          }
        });

      await runLimitedChunkTasks(incompleteChunks, {
        perFileConcurrency: session.maxPerFileConcurrency || UPLOAD_CHUNKED_PER_FILE_CONCURRENCY,
        globalSemaphore: globalChunkSemaphore,
        isCancelled,
      });

      if (isCancelled()) {
        return true;
      }

      const reconciliation = await this.reconcileUploadChunks(options.uploadJobId, options.uploadJobLogId);
      if (!reconciliation.finaliseReady) {
        throw new Error(reconciliation.blockingReason || "Chunked upload is not ready to finalise");
      }
      progressCallback(itemIndex + 1, file.name);
      return true;
    },
    
    uploadFile({ file, ...options }, itemIndex, progressCallback) {
      let gcs = null;
      let id = null;
      let chunkedUploadStarted = false;
      let expectedCrc32c = null;

      let abortController = null;
      const cancel = async () => {
        if (id !== null) {
          gcs?.cancel();
          if (abortController) {
            abortController.abort();
          }
          if (!chunkedUploadStarted) {
            await this.deleteFile({ id }, { showLoader: false }).catch(console.error); // not a critical error, but leaves dead files
          }
          id = null;
        }
      };

      return cancellable(async (isCancelled) => {
        abortController = new AbortController();
        try {
          if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
            const error = new Error(`${file.name} exceeds the maximum upload size of ${MAX_UPLOAD_FILE_SIZE_LABEL}.`);
            error.code = "UPLOAD_FILE_TOO_LARGE";
            throw error;
          }
          options.onClientStatus?.("CREATING_FILE");
          const createStartedAt = performance.now();
          const response = options.precreatedFileResponse || await this.createFile({ ...options, size: file.size, contentType: file.type });
          if (!options.precreatedFileResponse) {
            options.onClientTiming?.("createFile", {
              durationMs: Math.max(0, Math.round(performance.now() - createStartedAt)),
              size: file.size,
            });
          }
          id = response.id;
          options.onClientStatus?.("CREATING_FILE", { itemId: id, uploadId: response.uploadId });
          if (isCancelled()) {
            return;
          }
          if (file.size > 0) {
            const storageStartedAt = performance.now();
            options.onClientStatus?.("UPLOADING", { itemId: id, uploadId: response.uploadId });
            const shouldUseChunkedUpload = options.uploadJobId
              && options.uploadJobLogId
              && (response.chunkedUploadRequired || (file.size >= UPLOAD_CHUNKED_MIN_SIZE && isChunkedUploadEligible({
                uploadV2Active: options.uploadV2Active,
                uploadJobId: options.uploadJobId,
                uploadJobLogId: options.uploadJobLogId,
                fileSize: file.size,
                minimumChunkedUploadSize: UPLOAD_CHUNKED_MIN_SIZE,
              })));
            if (shouldUseChunkedUpload) {
              chunkedUploadStarted = true;
            }
            const usedChunkedUpload = shouldUseChunkedUpload
              && await this.uploadChunkedFile({
                file,
                response,
                options,
                itemIndex,
                progressCallback,
                signal: abortController.signal,
                isCancelled,
              });
            if (usedChunkedUpload) {
              // Chunked uploads compose temporary chunk objects server-side before finalise.
            } else if (GCS_UPLOAD_MODE === "library") {
              if (!response.uploadUrl) {
                const error = new Error("Upload URL is missing for direct file upload");
                error.code = "UPLOAD_CREATE_MISSING_URL";
                throw error;
              }
              const GcsUpload = await loadGcsUpload();
              gcs = new GcsUpload({
                id: response.uploadId,
                url: response.uploadUrl,
                file,
                contentType: file.type,
                chunkSize: UPLOAD_CHUNK_SIZE,
                onChunkUpload: (info) => {
                  options.onClientProgress?.(info);
                  const fileProgress = itemIndex + (info.uploadedBytes / info.totalBytes);
                  if (fileProgress < itemIndex + 1) {
                    progressCallback(fileProgress, file.name);
                  }
                },
              });
              await gcs.start();
              if (response.crcVerificationRequired) {
                expectedCrc32c = await crc32cHexForBlob(file);
              }
            } else {
              if (!response.uploadUrl) {
                const error = new Error("Upload URL is missing for direct file upload");
                error.code = "UPLOAD_CREATE_MISSING_URL";
                throw error;
              }
              const uploadResult = await uploadResumable({
                url: response.uploadUrl,
                file,
                contentType: file.type,
                chunkSize: UPLOAD_CHUNK_SIZE,
                signal: abortController.signal,
                isCancelled,
                maxRetries: 3,
                baseBackoffMs: 200,
                maxBackoffMs: 2000,
                calculateCrc: response.crcVerificationRequired,
                onChunkUpload: (info) => {
                  options.onClientProgress?.(info);
                  const fileProgress = itemIndex + (info.uploadedBytes / info.totalBytes);
                  if (fileProgress < itemIndex + 1) {
                    progressCallback(fileProgress, file.name);
                  }
                },
                onUploadProgress: (info) => {
                  options.onClientProgress?.(info);
                  const fileProgress = itemIndex + (info.uploadedBytes / info.totalBytes);
                  if (fileProgress < itemIndex + 1) {
                    progressCallback(fileProgress, file.name);
                  }
                },
              }).catch((err) => {
                console.error("GCS upload failed", {
                  itemId: id,
                  uploadId: response.uploadId,
                  error: sanitizeUploadError(err),
                });
                if (isCancelled()) {
                  return;
                }
                throw err;
              });
              expectedCrc32c = uploadResult?.crc32c || null;
            }
            options.onClientTiming?.("storageUpload", {
              durationMs: Math.max(0, Math.round(performance.now() - storageStartedAt)),
              size: file.size,
              chunked: !!usedChunkedUpload,
            });
          }
          if (!isCancelled() && !options.skipFinalise) {
            options.onClientStatus?.("FINALISING", { itemId: id, uploadId: response.uploadId });
            const finaliseStartedAt = performance.now();
            await this.finaliseFile({
              id,
              uploadJobId: options.uploadJobId,
              uploadJobLogId: options.uploadJobLogId,
              ...(expectedCrc32c ? { expectedCrc32c } : {}),
            });
            options.onClientTiming?.("finaliseFile", {
              durationMs: Math.max(0, Math.round(performance.now() - finaliseStartedAt)),
              size: file.size,
            });
          }

          return {
            id,
            uploadId: response.uploadId,
            expectedCrc32c,
            sensitiveNameDetection: response.sensitiveNameDetection || null,
          };

        } catch (error) {
          if (error?.name === "AbortError" || isCancelled()) {
            return;
          }
          // If someone is uploading an entire folder, we don't want to force them to have to deal with these manually!
          if (error.code !== 'DUPLICATE_ITEM') {
            await cancel();
            throw error;
          } else if (options.onClientStatus) {
            throw error;
          }
        } finally {
          id = null;
        }
      }, cancel);
    },
    chunkArray(inArray, chunkSize) {
      let returnArray = [];

      for (let i = 0;i<inArray.length;i+=chunkSize) {
        let currentChunk = inArray.slice(i, i + chunkSize);
        returnArray.push(currentChunk);
      }

      return returnArray;
    },
    async uploadFolderPaths(parentItem, paths, tags, metadata) {
      return await this.createPaths(parentItem, paths, tags, metadata);
    },
    buildUniqueFolderPaths(items) {
      const unique = (value, index, self) => self.indexOf(value) === index;
      return items
        .map((item) => {
          let parsedPath = item.subPath;
          if (parsedPath && parsedPath.endsWith(item.name)) {
            parsedPath = parsedPath.substring(0, parsedPath.lastIndexOf(item.name));
          }
          return parsedPath;
        })
        .filter(unique)
        .filter(String)
        .sort((a, b) => {
          const aDepth = a.split("/").filter(Boolean).length;
          const bDepth = b.split("/").filter(Boolean).length;
          if (aDepth !== bDepth) {
            return aDepth - bDepth;
          }
          return a.localeCompare(b);
        });
    },
    uploadChunk(chunk, chunkIndex, progressCallback, errorCallback) {
      let uploadFilePointers = [];
      const itemProgress = new Array(chunk.length).fill(0);
      const completedBeforeChunk = chunkIndex * NUMBER_OF_FILES_PER_CHUNK;
      const reportChunkProgress = (itemIndex, fileName, fraction) => {
        itemProgress[itemIndex] = Math.max(itemProgress[itemIndex] || 0, Math.max(0, Math.min(1, Number(fraction || 0))));
        const filesDone = completedBeforeChunk + itemProgress.reduce((total, value) => total + value, 0);
        progressCallback(filesDone, fileName);
      };
      
      return cancellable(async (isCancelled) => {

        let itemResults = [];
        if (isCancelled()) {
            return { chunkIndex, chunkSize: chunk.length, itemResults }
        }

        await Promise.all(chunk.map((item, itemIndex) => {

          // overall index across all files = offset for previous chunks + index within this chunk
          const overallIndex = (chunkIndex * NUMBER_OF_FILES_PER_CHUNK) + itemIndex;
          reportChunkProgress(itemIndex, item.file?.name || item.name || "", 0.01);
          const uploadFile = this.uploadFile(item, overallIndex, (currentIndex, fileName) => {
            reportChunkProgress(itemIndex, fileName, Number(currentIndex || 0) - overallIndex);
          });
          uploadFilePointers.push(uploadFile);
          
          const result = uploadFile.run()
            .then((uploadResult) => {
              reportChunkProgress(itemIndex, "", 1);
              return {
                itemIndex,
                success: true,
                sensitiveNameDetection: uploadResult?.result?.sensitiveNameDetection || null,
              };
            })
            .catch(error => {
              reportChunkProgress(itemIndex, "", 1);
              errorCallback(item, mapUploadError(error));
              return {itemIndex, success: false};
            });

          return result;
        }))
        .then((response) => {
          itemResults = response;
        })
        
        return { chunkIndex, numberOfFilesInChunk: chunk.length, itemResults }
      },
      () => uploadFilePointers.map(uploadFilePointer => uploadFilePointer?.cancel()));
    },
    uploadFiles(items, progressCallback, errorCallback) {
      let currentChunk = null;

      return cancellable(async (isCancelled) => {
        let anySuccess = false;
        let sensitiveDetections = [];
        
        if (items.length === 0) {
          return { anySuccess, sensitiveDetections };
        }

        const parentItem = items[0].parentItem;
        const tags = items[0].tags;
        const metadata = items[0].metadata;
        const allPaths = this.buildUniqueFolderPaths(items);
        const pathBatches = this.chunkArray(allPaths, 500);
        const totalPaths = allPaths.length;
        let pathsProcessed = 0;
        if (totalPaths > 0) {
          progressCallback(0, "", items.length, "creating-folders", {
            current: 0,
            total: totalPaths,
          });
        }
        for (let batchIndex = 0; batchIndex < pathBatches.length; batchIndex++) {
          const batch = pathBatches[batchIndex];
          const pathResponse = await this.uploadFolderPaths(parentItem, batch, tags, metadata);
          sensitiveDetections = sensitiveDetections.concat(
            (pathResponse?.sensitiveNameDetections || []).filter(detection => detection?.sensitiveNameDetected)
          );
          pathsProcessed += batch.length;
          if (totalPaths > 0) {
            progressCallback(0, "", items.length, "creating-folders", {
              current: pathsProcessed,
              total: totalPaths,
            });
          }
        }

        const itemsInChunks = this.chunkArray(items, NUMBER_OF_FILES_PER_CHUNK);
        const itemProgressCallback = (currentIndex, fileName) =>
          progressCallback(currentIndex, fileName, items.length, "uploading-files");
        
        for (let chunkIndex = 0; chunkIndex<itemsInChunks.length; chunkIndex++) {
        
          currentChunk = this.uploadChunk(itemsInChunks[chunkIndex], chunkIndex, itemProgressCallback, errorCallback)
          await currentChunk.run()
            .then(response => {
              const itemResults = response?.result?.itemResults || [];
              anySuccess = anySuccess || itemResults.filter(r => r?.success).length > 0;
              sensitiveDetections = sensitiveDetections.concat(
                itemResults
                  .map(r => r?.sensitiveNameDetection)
                  .filter(detection => detection?.sensitiveNameDetected)
              );
            })
            .catch(error => console.log("failed to process chunk", error));

          if (isCancelled()) {
            return;
          }
        }

        if (isCancelled()) {
          return false;
        }
        
        if (!anySuccess) {
           throw new Error('Failed to upload');
         }

        return { anySuccess, sensitiveDetections };
      }, () => currentChunk?.cancel());
    },
  },
};
